import { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { useAuth } from '../lib/auth-context';
import { useAdaptiveExperienceOptional } from '../context/AdaptiveExperienceContext';
import { deleteAllAdaptiveExperienceEvents } from '../lib/adaptive-experience-api';
import { clearAdaptiveSyncQueue } from '../lib/adaptive-sync-queue';
import { clearLocalAdaptiveEvents } from '../lib/adaptive-experience';
import { Cpu, Loader2, Shield, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';

/**
 * Privacy controls for adaptive personalization (web + Capacitor).
 */
export function AdaptivePrivacySettings() {
  const { accessToken } = useAuth();
  const adaptive = useAdaptiveExperienceOptional();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const clearAll = async () => {
    if (!accessToken) {
      toast.error('Sign in to manage cloud personalization data');
      return;
    }
    if (!confirm('Clear saved personalization signals on our servers and on this device? Your check-ins are not deleted.')) {
      return;
    }
    setBusy(true);
    try {
      await deleteAllAdaptiveExperienceEvents(accessToken);
      clearLocalAdaptiveEvents();
      clearAdaptiveSyncQueue();
      adaptive?.refresh();
      toast.success('Personalization history cleared');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not clear server data');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6 border-violet-200/80 bg-gradient-to-br from-violet-50/90 to-white dark:from-violet-950/40 dark:to-neutral-900 dark:border-violet-900/50">
      <div className="space-y-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-100 dark:bg-violet-900/50">
            <Cpu className="h-5 w-5 text-violet-700 dark:text-violet-300" aria-hidden />
          </div>
          <div>
            <h2 className="text-lg font-medium text-neutral-900 dark:text-neutral-100">Adaptive personalization</h2>
            <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
              TENSION learns which Relationship Intelligence tools you use and your RTI trend to prioritize the home
              hub and suite. Events are stored on this device and synced to your account when you are signed in.
            </p>
            {adaptive && (
              <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-500">
                Sync:{' '}
                <span className="font-medium text-neutral-700 dark:text-neutral-300">
                  {adaptive.syncState === 'loading'
                    ? 'Updating…'
                    : adaptive.syncState === 'error'
                      ? 'Paused (offline or server issue)'
                      : 'Up to date'}
                </span>
                {' · '}
                {adaptive.mergedEventCount} signal{adaptive.mergedEventCount === 1 ? '' : 's'} in your profile
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => navigate('/help/sync')}
          >
            <Shield className="h-4 w-4" aria-hidden />
            Sync & troubleshooting
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="gap-2"
            disabled={busy || !accessToken}
            onClick={() => void clearAll()}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" aria-hidden />}
            Clear personalization data
          </Button>
        </div>
      </div>
    </Card>
  );
}
